// Client de l'API d'Ekera v0 (/v0), typé depuis l'OpenAPI (schema.ts). Respecte les règles d'écriture de l'API :
// simulation d'abord, clé d'idempotence, If-Match (pas d'écrasement), jamais de suppression (aucune méthode DELETE).
import type { FournisseurIdentite } from '../auth/identite';
import type {
  Acces,
  Calcul,
  Carte,
  CatalogueSources,
  CollecteurDetail,
  CollecteurVue,
  RaisonRevocation,
  Contradiction,
  PieceLue,
  Variantes,
  DemandeEssai,
  ApplicationCible,
  EffetApplication,
  EssaiVariante,
  CorpsQuestion,
  DeclarationSource,
  Correction,
  ElementLu,
  EssaiVue,
  EntreeJournal,
  Evenement,
  Lien,
  Moi,
  Objet,
  DemandePlan,
  DemandeValidation,
  ReponsePlan,
  ReponseValidation,
  ReponseDefi,
  AssertionWebAuthn,
  AdresseDeclaree,
  CleEnrolee,
  EnvoiCode,
  CreationWebAuthn,
  EtatCle,
  OptionsCreation,
  VueCle,
  VueAdresse,
  VueAdresseAdmin,
  VueCleAdmin,
  Preuve,
  ReponseConsole,
  PlanEcriture,
  Validation,
  ResultatPublication,
  ResumeLivrable,
  VueLicences,
  VueLivrable,
  ReponseConstats,
  ReponseEcriture,
  ResumeCarte,
  EtatLecturePieces,
  NatureDepot,
  PieceDeposee,
  VueDepots,
  EtatEntretien,
  QuestionEntretien,
  QuestionDeleguee,
  ReponseDelegation,
  ResultatReponseEntretien,
  ResumeRapport,
  Sante,
  SourceDetail,
  SourceVue,
  TypeObjet,
  VueAnssi,
} from './types';
import { normaliserReponse } from './types';
import type { DepotRetour, DetailRetour, ListeRetours, PropositionRetour, ValidationRetour } from './types';
import type { AlertesCircuit, EtatCircuitLivrable, HistoriqueCircuit } from './types';
import type { ADecider, DecisionEtape, DefiCircuit, InstanceVue, MoiCircuit, ValeurAvis } from '../domaine/circuits';

export function identifiantRetourValide(id: string): boolean {
  return /^[0-9a-f]{32}$/.test(id);
}

function verifierIdentifiantRetour(id: string): void {
  if (!identifiantRetourValide(id)) throw new ErreurApi(404, 'retour_inconnu', 'Retour introuvable');
}

/** Erreur rendue par l'API ({code, message}) ou par le réseau. */
export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
    /** Secondes à attendre avant de réessayer (en-tête Retry-After d'un 429). */
    readonly reessayerApres?: number,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }

  /** L'élément a changé depuis la lecture (If-Match) : il faut relire avant d'écrire. */
  get estConflit(): boolean {
    return this.statut === 412;
  }
}

/**
 * Textes des deux motifs d'O119 : jeton d'une application qui n'est pas une application humaine du client, ou client qui n'a
 * pas déclaré ses applications humaines. Partagés par `/moi`, la page « Mes clés » et les refus 403.
 */
export const TEXTE_APPLICATION_NON_INTERACTIVE =
  'L’application utilisée pour cette connexion n’est pas une application humaine (compte de service ou script) : une clé de sécurité et la validation d’une écriture exigent une personne connectée par l’application web d’Ekera. Reconnectez-vous par cette application.';
export const TEXTE_APPLICATIONS_HUMAINES_NON_DECLAREES =
  'Votre organisation n’a pas déclaré ses applications humaines : Ekera ne peut donc pas savoir que cette connexion est celle d’une personne, et la validation exige une personne connectée. L’administrateur doit déclarer ces applications dans la configuration de connexion (OIDC) ; rien n’a été écrit.';

/**
 * Textes neutres des mêmes deux codes 403 pour les refus d'écriture en général : ces codes servent aussi à l'approbation d'un
 * collecteur ou à la validation d'un plan, où il n'est pas question de clé de sécurité (O144). Les écrans qui connaissent leur
 * contexte (collecteurs, clés) ont leur propre texte.
 */
export const TEXTE_REFUS_APPLICATION_NON_HUMAINE =
  'Cette action est réservée à une personne connectée par l’application web d’Ekera : la connexion utilisée n’est pas une application humaine (compte de service ou script). Reconnectez-vous par cette application ; rien n’a été écrit.';
export const TEXTE_REFUS_APPLICATIONS_NON_DECLAREES =
  'Votre organisation n’a pas déclaré ses applications humaines : Ekera ne peut pas savoir que cette connexion est celle d’une personne, et cette action en exige une. L’administrateur doit déclarer ces applications dans la configuration de connexion (OIDC) ; rien n’a été écrit.';

/** Explication en français de chaque refus, pour l'interface. */
export function expliquerErreur(e: unknown): string {
  if (!(e instanceof ErreurApi)) return e instanceof Error ? e.message : String(e);
  const parCode = expliquerCodeWebAuthn(e);
  if (parCode) return parCode;
  switch (e.statut) {
    case 0:
      return `API injoignable : ${e.message}`;
    case 401:
      return 'Session expirée ou jeton refusé par l’API : reconnectez-vous.';
    case 403:
      if (e.code === 'lecture_seule') return 'L’API est en lecture seule : aucun jeton d’écriture n’est configuré.';
      if (e.code === 'aucun_role') return 'Votre compte est reconnu, mais il n’a aucun rôle sur Ekera : voyez votre administrateur.';
      if (e.code === 'role_insuffisant') return `Votre rôle ne permet pas cette action (${e.message}).`;
      if (e.code === 'role_partage') return `Partager hors de l’entreprise exige le rôle « partage » (${e.message}).`;
      if (e.code === 'console_reservee') return 'La console d’interrogation est réservée aux personnes connectées par l’application : ce compte ou cette application n’y est pas admis.';
      if (e.code === 'validation_refusee') return `L’API refuse de valider à votre nom : ${e.message}.`;
      if (e.code === 'delegation_fermee')
        return 'Votre fournisseur d’identité ne certifie pas votre adresse de courriel : Ekera ne peut pas s’assurer que vous êtes la personne à qui la question a été déléguée, et ne vous l’ouvre donc pas. La personne qui l’a déléguée peut y répondre elle-même.';
      if (e.code === 'delegation_courriel_non_verifie')
        return 'Votre adresse de courriel n’est pas vérifiée par votre fournisseur d’identité : cette question déléguée ne peut pas vous être ouverte.';
      if (e.code === 'delegation_autre_destinataire')
        return 'Cette question a été déléguée à une autre personne : connectez-vous avec le compte dont l’adresse de courriel a été indiquée.';
      if (e.code === 'delegation_sans_destinataire') return 'Ce lien ne nomme aucun destinataire : il ne peut pas servir. Demandez un nouveau lien.';
      if (e.code === 'publication_refusee')
        return 'Le collecteur de votre site a refusé la publication (validation déjà utilisée, expirée ou sans geste, ou destination refusée) : rien n’a été écrit. Redemandez le plan.';
      return `Action interdite : ${e.message}`;
    case 404:
      if (e.code === 'plan_inconnu')
        return 'Ce plan n’existe plus pour vous (inconnu, expiré depuis longtemps ou demandé par quelqu’un d’autre) : redemandez le plan. Rien n’a été validé.';
      return `Introuvable : ${e.message}`;
    case 409:
      if (e.code === 'plan_deja_valide')
        return 'Ce plan a déjà été validé (une validation ne sert qu’une fois) : redemandez un plan si vous voulez écrire de nouveau. Aucune nouvelle validation n’a été émise.';
      if (e.code === 'empreinte_differente')
        return 'L’empreinte envoyée ne correspond pas à celle du plan gardé par l’API : rien n’a été validé. Redemandez le plan et relisez-le.';
      if (e.code === 'plan_altere')
        return 'Le plan gardé par l’API est altéré ou illisible : rien n’a été validé. Redemandez le plan.';
      if (e.code === 'conflit_outil')
        return 'L’outil de l’entreprise porte un fichier qu’Ekera n’écrase pas (déposé ou modifié par quelqu’un d’autre) : rien n’a été écrit. Publiez sous un autre titre, ou voyez avec l’administrateur de l’outil.';
      if (e.code === 'contenu_change')
        return 'Le contenu du document a changé depuis votre lecture : rien n’a été validé. Relisez-le, puis recommencez.';
      if (e.code === 'deja_deposee') return 'Ce fichier est déjà déposé (même contenu) : rien n’a été ajouté.';
      if (e.code === 'lecture_en_cours') return 'Une lecture des pièces est déjà en cours : attendez qu’elle se termine.';
      if (e.code === 'aucune_piece') return 'Aucune pièce à lire : déposez d’abord des documents.';
      if (e.code === 'trop_de_pieces' || e.code === 'volume_depasse') return `Limite de la carte atteinte : ${e.message}.`;
      if (e.code === 'deja_repondue') return 'Cette question a déjà une réponse : corrigez la carte si besoin.';
      if (e.code === 'delegation_utilisee') return 'Cette question déléguée a déjà reçu une réponse.';
      if (e.code === 'delegation_expiree') return 'Ce lien de délégation a expiré : demandez-en un nouveau.';
      if (e.code === 'delegation_fermee')
        return 'La délégation est fermée pour votre entreprise : votre fournisseur d’identité ne certifie pas les adresses de courriel (« email_verified »), et Ekera ne peut donc pas s’assurer que seul votre collègue répondra. Répondez vous-même, ou demandez à l’administrateur d’Ekera.';
      if (e.code === 'entretien_non_prepare') return 'L’entretien n’est pas encore préparé.';
      if (e.code === 'carte_existante') return 'Une carte porte déjà cet identifiant : choisissez-en un autre.';
      return `Refusé : ${e.message}`;
    case 410:
      if (e.code === 'plan_expire') return 'Ce plan a expiré avant d’être validé : redemandez-le et relisez-le. Rien n’a été validé.';
      return `Disparu : ${e.message}`;
    case 429:
      if (e.code === 'planification_en_cours')
        return 'Une demande de plan est déjà en cours pour vous : attendez qu’elle se termine (le collecteur de votre site calcule le plan), puis réessayez.';
      if (e.code === 'plans_en_attente_trop_nombreux')
        return 'Trop de plans gardés par l’API attendent déjà une validation (pour vous ou pour votre entreprise) : validez ou laissez expirer ceux qui sont ouverts (quelques minutes), puis redemandez un plan. Rien n’a été écrit.';
      if (e.code === 'debit_depasse')
        return e.reessayerApres
          ? `Trop de questions posées : réessayez dans ${e.reessayerApres} s.`
          : 'Trop de questions posées : réessayez dans quelques instants.';
      if (e.code === 'trop_de_rapports') return 'Trop de rapports générés cette heure-ci : réessayez plus tard.';
      if (e.reessayerApres) return `Trop de demandes : réessayez dans ${e.reessayerApres} s.`;
      return 'Trop d’appels simultanés : attendez un instant, puis réessayez.';
    case 412:
      return 'L’élément a changé depuis votre lecture. Relisez-le et refaites la simulation.';
    case 422:
      // Le message de l'API ne recopie jamais la valeur reçue ; l'interface non plus.
      if (e.code === 'nature_incompatible' || e.code === 'nature_requise') return `Fichier refusé : ${e.message}`;
      if (e.code === 'destinataire_invalide') return 'Indiquez le courriel professionnel du collègue (par exemple prenom.nom@entreprise.fr).';
      if (e.code === 'secret_refuse')
        return 'Un mot de passe, un jeton ou une clé a été refusé : Ekera n’accepte jamais de secret, seulement la référence du secret dans votre coffre. Rien n’a été enregistré.';
      if (e.code === 'cle_reutilisee') return 'Cette clé d’idempotence a déjà servi pour une autre écriture.';
      if (e.code === 'logiciel_inconnu') return `Cet outil n’est pas pris en charge : ${e.message}.`;
      if (e.code === 'ecriture_interdite') return `Cette écriture est interdite, elle ne sera pas planifiée : ${e.message}.`;
      return `Valeurs refusées : ${e.message}`;
    case 413:
      if (e.code === 'trop_grande') return `Fichier trop grand : ${e.message}.`;
      return 'La question est trop longue (500 caractères au plus).';
    case 415:
      return `Fichier refusé : ${e.message}`;
    case 428:
      return 'La version lue est obligatoire pour cette écriture (If-Match).';
    case 502:
      if (e.code === 'plan_non_authentifie')
        return 'Le plan rendu par le collecteur n’a pas pu être authentifié : rien n’est validé. Vérifiez la clé du collecteur avec votre administrateur.';
      if (e.code === 'collecteur_injoignable')
        return 'Le collecteur sur votre site ne répond pas : le plan n’a pas pu être calculé. Réessayez, ou vérifiez que le collecteur est en marche.';
      if (e.code === 'resultat_non_authentifie')
        return 'Le résultat rendu par le collecteur n’a pas pu être authentifié : la publication n’est pas enregistrée dans Ekera. Vérifiez le document dans l’outil, et la clé du collecteur avec votre administrateur.';
      if (e.code === 'outil_indisponible')
        return 'L’outil documentaire de l’entreprise n’a pas répondu au collecteur comme attendu : rien n’est enregistré. Réessayez plus tard.';
      return `Erreur 502 : ${e.message}`;
    case 504:
      if (e.code === 'collecteur_delai_depasse')
        return 'Le collecteur de votre site n’a pas répondu à temps : la demande est abandonnée, aucune écriture n’a été validée ni faite. Vous pouvez réessayer ; vérifiez d’abord que le collecteur est en marche chez vous.';
      return `Délai dépassé : ${e.message}`;
    case 503:
      if (e.code === 'publication_non_configuree')
        return 'Aucun outil documentaire de l’entreprise n’est configuré : consultez le document ici, ou téléchargez-le.';
      if (e.code === 'publication_sans_collecteur')
        return 'Aucun collecteur de votre site ne sait publier vers cet outil : la publication se fait depuis votre réseau, avec le compte de l’outil gardé chez vous. Rien n’a été publié ; téléchargez le document en attendant.';
      if (e.code === 'validation_indisponible')
        return 'La validation des écritures n’est pas configurée sur cette installation (clé de validation absente).';
      if (e.code === 'planification_indisponible')
        return 'Aucun collecteur n’est configuré pour calculer le plan sur cette installation : la validation n’est pas disponible.';
      return `Service indisponible : ${e.message}`;
    default:
      return `Erreur ${e.statut} : ${e.message}`;
  }
}

/** « 15 minutes », « 1 heure » : délai d'un 429 (Retry-After) en français. */
export function delaiLisible(secondes: number): string {
  if (secondes >= 3600) {
    const h = Math.round(secondes / 3600);
    return `${h} heure${h > 1 ? 's' : ''}`;
  }
  if (secondes >= 60) {
    const m = Math.round(secondes / 60);
    return `${m} minute${m > 1 ? 's' : ''}`;
  }
  return `${Math.max(1, Math.round(secondes))} seconde${secondes > 1 ? 's' : ''}`;
}

/**
 * Messages des codes d'erreur de la validation par geste WebAuthn (O100), un par code du tableau de la fiche T1. Le
 * message de l'API n'est jamais recopié (il pourrait nommer ce qu'il ne faut pas dire) : un refus de geste ne dit pas
 * pourquoi, à dessein.
 */
export function expliquerCodeWebAuthn(e: ErreurApi): string | null {
  switch (e.code) {
    case 'cle_non_enrolee':
      return 'Vous n’avez aucune clé de sécurité active : une clé encore en attente d’approbation, ou dans son délai de sécurité, ne sert pas encore. Enregistrez-en une dans « Mes clés ». Rien n’a été validé.';
    case 'geste_refuse':
      return 'Geste refusé : l’API n’a pas reconnu votre geste pour cette demande. Rien n’est signé ni validé. Demandez un nouveau défi et recommencez : un seul défi de validation reste ouvert par personne, donc un défi demandé pour un autre plan (autre onglet, autre écran) remplace celui-ci. Si cela se répète, prévenez votre administrateur.';
    case 'adresse_alerte_invalide':
      return 'Ekera n’a aucune adresse de courriel utilisable pour vous alerter (adresse du fournisseur d’identité absente, non vérifiée ou hors du domaine de votre organisation, ou adresse déclarée non confirmée) : rien n’a été enregistré ni validé. Voyez la section « Adresse d’alerte » de l’écran « Mes clés » : un administrateur peut déclarer votre adresse, que vous confirmez ensuite avec un code reçu par courriel.';
    case 'enrolement_refuse':
      return 'Clé non admise : elle ne remplit pas ce qu’Ekera exige (clé de sécurité certifiée, non synchronisée, avec vérification de la personne). Rien n’a été enregistré ; essayez une autre clé.';
    case 'code_amorcage_invalide':
      return 'Code d’amorçage refusé (inconnu, expiré, déjà utilisé, destiné à un autre compte ou verrouillé). Vérifiez-le auprès de la personne qui vous l’a remis, hors de l’application. Rien n’a été enregistré.';
    case 'code_verification_faux':
      return 'Code de vérification faux : demandez-le de nouveau à la personne, de vive voix ou par téléphone. Au bout de trois erreurs, la demande de clé est refusée d’office. Rien n’a été approuvé.';
    case 'approbation_par_soi_meme':
      return 'Vous ne pouvez pas approuver votre propre clé : un autre administrateur doit le faire.';
    case 'cle_inconnue':
      return 'Cette clé n’existe pas (ou plus) pour votre entreprise : relisez la liste.';
    case 'carte_inconnue':
      return 'Cette carte n’existe pas pour votre entreprise.';
    case 'cle_pas_en_attente':
      return 'Cette clé n’est plus en attente d’approbation : relisez la liste.';
    case 'cle_deja_revoquee':
      return 'Cette clé est déjà révoquée.';
    case 'defi_expire':
      return 'Le défi a expiré avant votre geste (il ne vaut que deux minutes) : rien n’est signé. Recommencez, un nouveau défi sera demandé.';
    case 'demande_expiree':
      return 'Cette demande de clé a expiré (72 heures sans approbation) : la personne doit refaire l’enregistrement.';
    case 'corps_trop_gros':
      return 'La requête est trop grosse pour l’API (16 Kio au plus) : rien n’a été fait.';
    case 'longueur_requise':
      return 'La requête n’indique pas sa taille : l’API la refuse. Rien n’a été fait.';
    case 'gestes_refuses_trop_nombreux':
      return `Trop de gestes ont été refusés pour votre compte : les validations et les défis sont bloqués pendant ${delaiLisible(e.reessayerApres ?? 900)}. Si ce n’étaient pas vous, prévenez votre administrateur.`;
    case 'defis_trop_nombreux':
      return `Trop de défis demandés pour ce plan (cinq au plus) : attendez ${delaiLisible(e.reessayerApres ?? 300)}, puis réessayez. Le plan reste validable ; rien n’est signé.`;
    case 'defis_session_trop_nombreux':
      return `Trop de demandes de geste depuis cette session (quinze par heure) : attendez ${delaiLisible(e.reessayerApres ?? 3600)}, ou ouvrez une nouvelle session. Le plan reste validable ; rien n’est signé. Si ce n’est pas vous qui avez fait ces demandes, prévenez votre administrateur : votre accès est peut-être utilisé par quelqu’un d’autre.`;
    case 'defis_compte_trop_nombreux':
      return `Trop de demandes de geste pour votre compte (trente par heure) : attendez ${delaiLisible(e.reessayerApres ?? 3600)}, puis réessayez. Le plan reste validable ; rien n’est signé. Si ce n’est pas vous qui avez fait ces demandes, prévenez votre administrateur : votre accès est peut-être utilisé par quelqu’un d’autre.`;
    case 'enrolements_trop_nombreux':
      return `Trop de clés demandées pour votre compte ou votre entreprise : attendez ${delaiLisible(e.reessayerApres ?? 3600)}, ou faites approuver ou refuser les demandes en attente.`;
    // adresse d'alerte (décision O107, chemin B) : un message par code de la fiche, jamais le texte de l'API
    case 'code_adresse_invalide':
      return 'Code refusé : il est faux, périmé, déjà utilisé trois fois, ou aucun code n’a été envoyé. L’API ne dit pas lequel. Demandez un nouveau code (il vaut 15 minutes), lisez-le dans le courriel reçu à cette adresse et saisissez-le de nouveau. L’adresse n’est pas confirmée.';
    case 'essais_code_trop_nombreux':
      return `Trop d’essais de code : la confirmation est bloquée pendant ${delaiLisible(e.reessayerApres ?? 900)}. Aucune adresse n’est confirmée. Réessayez ensuite avec un code reçu par courriel, ou demandez-en un nouveau.`;
    case 'codes_adresse_trop_nombreux':
      return `Trop de codes envoyés pour cette adresse (cinq au plus) : attendez ${delaiLisible(e.reessayerApres ?? 3600)}, ou faites annuler la déclaration puis déclarer de nouveau par un administrateur.`;
    case 'declarations_adresse_trop_nombreuses':
      return `Trop d’adresses déclarées en peu de temps : attendez ${delaiLisible(e.reessayerApres ?? 3600)}, puis réessayez. Rien n’a été déclaré.`;
    case 'adresse_hors_domaine':
      return 'Cette adresse n’est pas d’un domaine déclaré pour votre entreprise : Ekera n’envoie d’alertes de sécurité qu’à une boîte de votre organisation. Rien n’a été enregistré ni envoyé. Vérifiez l’adresse ; si le domaine manque, voyez le déploiement d’Ekera.';
    case 'adresse_invalide':
      return 'Cette adresse n’est pas une adresse de courriel acceptée (lettres latines, chiffres, point, tiret ; 254 caractères au plus). Rien n’a été enregistré ni envoyé.';
    case 'adresse_inconnue':
      return 'Cette adresse n’existe pas (ou plus) pour vous : relisez la liste.';
    case 'adresse_pas_en_attente':
      return 'Cette adresse n’attend plus de confirmation (déjà confirmée, annulée ou remplacée) : relisez la liste.';
    case 'declaration_expiree':
      return 'Cette déclaration a expiré (72 heures sans confirmation) : un administrateur doit déclarer l’adresse de nouveau.';
    case 'declaration_pour_soi_meme':
      return 'Vous ne pouvez pas déclarer votre propre adresse : un autre administrateur doit le faire (ou l’opérateur d’Ekera pour le premier administrateur).';
    case 'declaration_en_cours':
      return 'Une déclaration attend déjà la confirmation de cette personne : annulez-la, ou attendez qu’elle expire (72 heures), avant d’en faire une autre.';
    case 'changement_en_cours':
      return 'Un changement d’adresse est déjà en carence pour cette personne : annulez-le ou attendez son activation avant d’en déclarer un autre.';
    case 'adresse_deja_confirmee':
      return 'Cette personne a déjà cette adresse confirmée : rien à déclarer.';
    case 'adresse_deja_attribuee':
      return 'Cette boîte est déjà attribuée à une autre personne de l’entreprise : une adresse ne sert qu’à une personne. Rien n’a été enregistré.';
    case 'adresse_pas_annulable':
      return 'Cette adresse ne peut plus être annulée (déjà utilisée, annulée ou remplacée). Pour une adresse utilisée, un administrateur la désactive.';
    case 'adresse_pas_active':
      return 'Cette adresse n’est pas une adresse utilisée : on ne peut désactiver qu’une adresse confirmée et en service. Relisez la liste.';
    case 'adresse_protege_une_cle':
      return 'Cette adresse est la seule qui protège une clé de sécurité de cette personne : déclarez d’abord sa nouvelle adresse (elle la remplacera), puis désactivez l’ancienne si besoin. Rien n’a été désactivé.';
    case 'webauthn_indisponible':
      return 'La validation par clé de sécurité n’est pas configurée sur cette installation : aucune validation n’est possible, et il n’existe aucun moyen de la contourner. Voyez votre administrateur.';
    case 'compte_de_service':
      return TEXTE_REFUS_APPLICATION_NON_HUMAINE;
    case 'applications_humaines_non_declarees':
      return TEXTE_REFUS_APPLICATIONS_NON_DECLAREES;
    default:
      return null;
  }
}

export interface OptionsEcriture {
  /** Vrai : l'API rend la différence exacte sans rien écrire. */
  simulation: boolean;
  /** Clé d'idempotence (obligatoire hors simulation) : la même pour un nouvel essai de la même écriture. */
  cleIdempotence?: string;
  /** ETag de l'élément lu (« "3" ») : obligatoire pour modifier, valider, retirer. */
  ifMatch?: string;
}

export interface ResultatEcriture {
  reponse: ReponseEcriture;
  statut: number;
  rejoue: boolean;
}

export class ClientEkera {
  constructor(
    private readonly identite: FournisseurIdentite,
    private readonly base = '',
    private readonly recuperer: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async appel(chemin: string, init: RequestInit = {}): Promise<Response> {
    const enTetes = new Headers(init.headers);
    for (const [k, v] of Object.entries(await this.identite.enTetes())) enTetes.set(k, v);
    let r: Response;
    try {
      r = await this.recuperer(`${this.base}${chemin}`, { ...init, headers: enTetes });
    } catch (e) {
      throw new ErreurApi(0, 'reseau', e instanceof Error ? e.message : String(e));
    }
    if (!r.ok && r.status !== 304) {
      let code = 'erreur';
      let message = r.statusText || `statut ${r.status}`;
      try {
        const corps = (await r.json()) as { code?: string; message?: string; detail?: unknown };
        if (corps.code) code = corps.code;
        if (corps.message) message = corps.message;
        else if (corps.detail) message = JSON.stringify(corps.detail);
      } catch {
        /* corps illisible : on garde le statut */
      }
      const attente = Number(r.headers.get('Retry-After'));
      throw new ErreurApi(r.status, code, message, Number.isFinite(attente) && attente > 0 ? attente : undefined);
    }
    return r;
  }

  private async json<T>(chemin: string): Promise<T> {
    return (await (await this.appel(chemin)).json()) as T;
  }

  private c(cid: string): string {
    return `/v0/cartes/${encodeURIComponent(cid)}`;
  }

  sante(): Promise<Sante> {
    return this.json('/v0/sante');
  }
  /** L'appelant vu par l'API : identité vérifiée et rôles Ekera déduits du jeton. */
  moi(): Promise<Moi> {
    return this.json('/v0/moi');
  }
  /** Journal d'accès du client (rôle administration) : qui a lu ou écrit quoi, quand. */
  acces(limite = 100): Promise<Acces[]> {
    return this.json(`/v0/acces?limite=${limite}`);
  }
  cartes(): Promise<ResumeCarte[]> {
    return this.json('/v0/cartes');
  }
  // Retours de l'auteur, sous l'organisation du jeton ; aucun identifiant de client dans la requête.
  async retours(apres?: string): Promise<ListeRetours> {
    const r = await this.appel(`/v0/retours${apres ? `?apres=${encodeURIComponent(apres)}` : ''}`, { cache: 'no-store' });
    return await r.json() as ListeRetours;
  }
  async retour(id: string): Promise<DetailRetour> {
    verifierIdentifiantRetour(id);
    const r = await this.appel(`/v0/retours/${encodeURIComponent(id)}`, { cache: 'no-store' });
    return await r.json() as DetailRetour;
  }
  async deposerRetour(corps: DepotRetour, cle: string): Promise<{ id: string; etat: 'recu'; envoi_disponible: false }> {
    const r = await this.appel('/v0/retours', {
      method: 'POST', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': cle },
      body: JSON.stringify(corps),
    });
    return await r.json() as { id: string; etat: 'recu'; envoi_disponible: false };
  }
  async proposerRetour(id: string, corps: PropositionRetour = {}): Promise<DetailRetour> {
    verifierIdentifiantRetour(id);
    return this.poster(`/v0/retours/${encodeURIComponent(id)}/propositions`, corps);
  }
  async validerRetour(id: string, corps: ValidationRetour): Promise<DetailRetour> {
    verifierIdentifiantRetour(id);
    return this.poster(`/v0/retours/${encodeURIComponent(id)}/validation`, corps);
  }
  carte(cid: string): Promise<Carte> {
    return this.json(this.c(cid));
  }
  objets(cid: string, f: { type?: TypeObjet; inclureRetires?: boolean } = {}): Promise<Objet[]> {
    const q = new URLSearchParams();
    if (f.type) q.set('type', f.type);
    if (f.inclureRetires) q.set('inclure_retires', 'true');
    const s = q.toString();
    return this.json(`${this.c(cid)}/objets${s ? `?${s}` : ''}`);
  }
  liens(cid: string, inclureRetires = false): Promise<Lien[]> {
    return this.json(`${this.c(cid)}/liens${inclureRetires ? '?inclure_retires=true' : ''}`);
  }
  /** Lit un objet ou un lien avec son ETag, juste avant d'écrire. */
  async element(cid: string, id: string, nature: 'objet' | 'lien'): Promise<ElementLu<Objet | Lien>> {
    const r = await this.appel(`${this.c(cid)}/${nature === 'objet' ? 'objets' : 'liens'}/${encodeURIComponent(id)}`);
    const etag = r.headers.get('ETag');
    if (!etag) throw new ErreurApi(r.status, 'etag_absent', 'l’API n’a pas rendu de version (ETag)');
    return { element: (await r.json()) as Objet | Lien, etag };
  }
  contradictions(cid: string): Promise<Contradiction[]> {
    return this.json(`${this.c(cid)}/contradictions`);
  }
  pieces(cid: string): Promise<PieceLue[]> {
    return this.json(`${this.c(cid)}/pieces`);
  }
  variantes(cid: string): Promise<Variantes> {
    return this.json(`${this.c(cid)}/variantes`);
  }
  /**
   * Essai d'une variante modifiée (gestes ajoutés ou retirés, hypothèse sur un passage) : l'API la recalcule par le
   * moteur et n'écrit RIEN (ni carte, ni journal des écritures, ni décision). Lecture seule, sans cache.
   */
  essayerVariante(cid: string, demande: DemandeEssai): Promise<EssaiVariante> {
    return this.poster(`${this.c(cid)}/variantes/essai`, demande);
  }
  /**
   * Effet d'une application cible proposée (POST …/applications-cibles/essai) : chiffres du moteur, lecture seule,
   * rien n'est écrit. Une demande par variante (30 par minute et par personne, comptées avec les essais de variantes).
   */
  effetApplication(cid: string, application: ApplicationCible): Promise<EffetApplication> {
    return this.poster(`${this.c(cid)}/applications-cibles/essai`, application);
  }
  constats(cid: string): Promise<ReponseConstats> {
    return this.json(`${this.c(cid)}/constats`);
  }
  scenarios(cid: string): Promise<Calcul> {
    return this.json(`${this.c(cid)}/scenarios`);
  }
  preuves(cid: string, element?: string): Promise<Preuve[]> {
    return this.json(`${this.c(cid)}/preuves${element ? `?element=${encodeURIComponent(element)}` : ''}`);
  }
  journal(cid: string): Promise<EntreeJournal[]> {
    return this.json(`${this.c(cid)}/journal`);
  }
  evenements(cid: string): Promise<Evenement[]> {
    return this.json(`${this.c(cid)}/evenements`);
  }
  /**
   * Interroge la carte (lecture seule : la console ne modifie rien). La question ne va que dans le corps de la requête :
   * jamais dans l'adresse, jamais dans un stockage du navigateur.
   */
  async poserQuestion(cid: string, corps: CorpsQuestion): Promise<ReponseConsole> {
    const r = await this.appel(`${this.c(cid)}/questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    return normaliserReponse((await r.json()) as Parameters<typeof normaliserReponse>[0]);
  }
  vueAnssi(cid: string, vue: VueAnssi): Promise<string> {
    return this.appel(`${this.c(cid)}/exports/anssi/${vue}`).then((r) => r.text());
  }

  /** Télécharge un export (OCSF, CycloneDX, OSCAL, STIX, ArchiMate, vue ANSSI) tel que l'API le rend. */
  async exporter(cid: string, chemin: string): Promise<{ contenu: Blob; type: string }> {
    const r = await this.appel(`${this.c(cid)}/exports/${chemin}`);
    return { contenu: await r.blob(), type: r.headers.get('Content-Type') ?? 'application/octet-stream' };
  }

  /**
   * Premier temps (O95) — POST /plans : envoie la demande (jamais de plan) ; l'API fait planifier le collecteur du
   * client, authentifie le plan, le GARDE pour cette personne et le rend avec son empreinte et son échéance. Rien
   * n'est signé. Jamais mis en cache.
   */
  async demanderPlan(cid: string, d: Pick<DemandePlan, 'logiciel' | 'table' | 'demande'>): Promise<ReponsePlan> {
    const r = await this.appel(`${this.c(cid)}/plans`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logiciel: d.logiciel, table: d.table, demande: d.demande }),
    });
    return (await r.json()) as ReponsePlan;
  }

  /**
   * Second temps (O95) — POST /validations : désigne le plan gardé (`plan_id`) et l'empreinte que la personne a vue ;
   * l'API ne signe que ce plan-là (même personne, non expiré, jamais validé) et rend {plan, validation}. Usage
   * unique côté API : un nouvel appel sur le même plan est refusé (409). Jamais mis en cache.
   */
  async valider(
    cid: string,
    d: Pick<DemandeValidation, 'plan_id' | 'empreinte' | 'defi_id' | 'assertion'> & { attestation_id?: string | null },
    commentaire?: string,
  ): Promise<ReponseValidation> {
    return this.poster(`${this.c(cid)}/validations`, {
      plan_id: d.plan_id,
      empreinte: d.empreinte,
      commentaire: commentaire?.trim() || null,
      defi_id: d.defi_id,
      assertion: d.assertion,
      // l'attestation de fin de circuit (CV-2, CV-3) : sans elle, l'API refuse de signer (circuit_non_termine)
      ...(d.attestation_id ? { attestation_id: d.attestation_id } : {}),
    });
  }

  /** POST …/plans/{plan_id}/defi (O100) : le défi lié à ce plan ; le geste se fait dessus, une seule fois. Corps vide. */
  defiPlan(cid: string, planId: string): Promise<ReponseDefi> {
    return this.poster(`${this.c(cid)}/plans/${encodeURIComponent(planId)}/defi`);
  }

  // ------------------------------------------------------------ circuit de validation (CV-3)
  // Aucune décision ne part sans défi ni geste ; aucune n'est tirée d'un lien ou d'un paramètre d'URL.

  circuitsMoi(): Promise<MoiCircuit> {
    return this.json('/v0/circuits/moi');
  }
  aDecider(): Promise<ADecider[]> {
    return this.json('/v0/circuits/a-decider');
  }
  instanceCircuit(iid: string): Promise<InstanceVue> {
    return this.json(`/v0/circuits/instances/${encodeURIComponent(iid)}`);
  }
  attestationCircuit(iid: string): Promise<Record<string, unknown>> {
    return this.json(`/v0/circuits/instances/${encodeURIComponent(iid)}/attestation`);
  }
  /** Une page de l'historique (50 par défaut) ; ``apres`` = ``suivant`` de la page précédente. */
  historiqueCircuit(apres?: string | null): Promise<HistoriqueCircuit> {
    return this.json(apres ? `/v0/circuits/instances?apres=${encodeURIComponent(apres)}` : '/v0/circuits/instances');
  }
  alertesCircuit(): Promise<AlertesCircuit> {
    return this.json('/v0/circuits/alertes');
  }
  configurationCircuit(): Promise<Record<string, unknown>> {
    return this.json('/v0/circuits/configuration');
  }
  journalCircuit(): Promise<Record<string, unknown>> {
    return this.json('/v0/circuits/journal');
  }
  simulationCircuit(d: { type_objet: string; carte: string; objet?: string; plan_id?: string }): Promise<Record<string, unknown>> {
    return this.poster('/v0/circuits/simulation', d);
  }
  ouvrirInstance(d: { type_objet: 'ecriture_outil' | 'decision_variante'; carte: string; objet?: string; plan_id?: string; precedente?: string }): Promise<InstanceVue> {
    return this.poster('/v0/circuits/instances', d);
  }
  /** Premier temps d'une décision d'étape : le défi lié à l'instance, l'étape, l'empreinte vue et LA décision. */
  defiDecision(iid: string, etape: string, d: { decision: DecisionEtape; avis?: ValeurAvis | null; empreinte_objet: string; delegation_id?: string | null; motif?: string | null }): Promise<DefiCircuit> {
    return this.poster(`/v0/circuits/instances/${encodeURIComponent(iid)}/etapes/${encodeURIComponent(etape)}/defi`, d);
  }
  decider(
    iid: string,
    etape: string,
    d: { decision: DecisionEtape; avis?: ValeurAvis | null; empreinte_objet: string; delegation_id?: string | null; motif?: string | null; defi_id: string; assertion: AssertionWebAuthn },
  ): Promise<InstanceVue> {
    return this.poster(`/v0/circuits/instances/${encodeURIComponent(iid)}/etapes/${encodeURIComponent(etape)}/decision`, d);
  }
  /** Défi d'un acte du circuit (retrait, délégation, absence, suspension, levée, proposition de configuration). */
  defiActe(acte: string, cible: string | null, parametres: Record<string, unknown>): Promise<DefiCircuit> {
    return this.poster('/v0/circuits/gestes', { acte, cible, parametres });
  }
  retirerInstance(iid: string, d: { motif: string | null; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster(`/v0/circuits/instances/${encodeURIComponent(iid)}/retrait`, d);
  }
  deleguer(d: { parametres: Record<string, unknown>; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster('/v0/circuits/delegations', d);
  }
  finDelegation(did: string): Promise<Record<string, unknown>> {
    return this.poster(`/v0/circuits/delegations/${encodeURIComponent(did)}/fin`);
  }
  declarerAbsence(d: { parametres: { fin: string }; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster('/v0/circuits/absences', d);
  }
  suspendre(d: { parametres: Record<string, unknown>; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster('/v0/circuits/suspensions', d);
  }
  demanderLevee(sid: string, d: { parametres: Record<string, unknown>; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster(`/v0/circuits/suspensions/${encodeURIComponent(sid)}/levee`, d);
  }
  approuverLevee(lid: string, d: { defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster(`/v0/circuits/levees/${encodeURIComponent(lid)}/approbation`, d);
  }
  proposerConfiguration(d: { configuration: Record<string, unknown>; defi_id: string; assertion: AssertionWebAuthn }): Promise<Record<string, unknown>> {
    return this.poster('/v0/circuits/configurations', d);
  }
  annulerCarence(version: number, motif: string | null): Promise<Record<string, unknown>> {
    return this.poster(`/v0/circuits/configurations/${version}/annulation`, { motif });
  }
  etatCircuitObjet(cid: string, oid: string): Promise<{ etat: string; [k: string]: unknown }> {
    return this.json(`${this.c(cid)}/objets/${encodeURIComponent(oid)}/circuit`);
  }
  /** Soumet la publication d'un livrable validé au circuit du client (POST …/livrables/{l}/circuit). */
  soumettrePublication(cid: string, lid: string): Promise<InstanceVue> {
    return this.poster(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/circuit`);
  }
  etatCircuitLivrable(cid: string, lid: string): Promise<EtatCircuitLivrable> {
    return this.json(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/circuit`);
  }

  /** POST JSON sans cache (rien du geste, du défi ni de l'assertion n'est gardé par le navigateur). */
  private async poster<T>(chemin: string, corps?: unknown): Promise<T> {
    const r = await this.appel(chemin, {
      method: 'POST',
      cache: 'no-store',
      headers: corps === undefined ? {} : { 'Content-Type': 'application/json' },
      body: corps === undefined ? undefined : JSON.stringify(corps),
    });
    return (await r.json()) as T;
  }

  // ------------------------------------------------------------ clés WebAuthn (docs/PIVOT-API.md § 8.14)

  /** Les clés de la personne connectée (avec le code de vérification des clés en attente). */
  async mesCles(): Promise<VueCle[]> {
    const r = await this.appel('/v0/moi/cles', { cache: 'no-store' });
    return (await r.json()) as VueCle[];
  }
  /** Premier temps de l'enrôlement : l'API rend les options de création (jamais gardées ici). */
  optionsEnrolement(nom: string): Promise<OptionsCreation> {
    return this.poster('/v0/moi/cles/options', { nom });
  }
  /** Second temps : la réponse du navigateur (`reponse`), avec le code d'amorçage s'il y en a un. */
  enroler(demandeId: string, reponse: CreationWebAuthn, codeAmorcage?: string): Promise<CleEnrolee> {
    return this.poster('/v0/moi/cles', {
      demande_id: demandeId,
      reponse,
      ...(codeAmorcage ? { code_amorcage: codeAmorcage } : {}),
    });
  }
  optionsAjout(cleId: string): Promise<ReponseDefi> {
    return this.poster(`/v0/moi/cles/${encodeURIComponent(cleId)}/ajout/options`);
  }
  ajouter(cleId: string, defiId: string, assertion: AssertionWebAuthn): Promise<EtatCle> {
    return this.poster(`/v0/moi/cles/${encodeURIComponent(cleId)}/ajout`, { defi_id: defiId, assertion });
  }
  /** Révoque une de ses clés, sans geste (F9) : motif obligatoire, la clé reste dans l'historique. */
  revoquerMaCle(cleId: string, motif: string): Promise<EtatCle> {
    return this.poster(`/v0/moi/cles/${encodeURIComponent(cleId)}/revocation`, { motif });
  }
  /** Administration : les clés du client (jamais le code de vérification). */
  async clesDuClient(etat?: string): Promise<VueCleAdmin[]> {
    const r = await this.appel(`/v0/cles${etat ? `?etat=${encodeURIComponent(etat)}` : ''}`, { cache: 'no-store' });
    return (await r.json()) as VueCleAdmin[];
  }
  optionsApprobation(cleId: string): Promise<ReponseDefi> {
    return this.poster(`/v0/cles/${encodeURIComponent(cleId)}/approbation/options`);
  }
  approuver(cleId: string, defiId: string, assertion: AssertionWebAuthn, codeVerification: string): Promise<EtatCle> {
    return this.poster(`/v0/cles/${encodeURIComponent(cleId)}/approbation`, {
      defi_id: defiId,
      assertion,
      code_verification: codeVerification,
    });
  }
  refuserCle(cleId: string, motif: string): Promise<EtatCle> {
    return this.poster(`/v0/cles/${encodeURIComponent(cleId)}/refus`, { motif });
  }
  revoquerCle(cleId: string, motif: string): Promise<EtatCle> {
    return this.poster(`/v0/cles/${encodeURIComponent(cleId)}/revocation`, { motif });
  }

  // ------------------------------------------------------------ adresse d'alerte (docs/PIVOT-API.md § 8.14, O107 chemin B)
  // Le code de confirmation n'est dans aucune réponse : il n'existe que dans le courriel. Il part dans le corps de la
  // confirmation (jamais dans l'adresse de la requête), sans cache, et l'application ne le garde pas.

  /** Personne : demander (ou redemander) l'envoi du code à l'adresse déclarée. */
  codeAdresse(adresseId: string): Promise<EnvoiCode> {
    return this.poster(`/v0/moi/adresses/${encodeURIComponent(adresseId)}/code`);
  }
  /** Personne : confirmer l'adresse avec le code reçu par courriel. */
  confirmerAdresse(adresseId: string, code: string): Promise<VueAdresse> {
    return this.poster(`/v0/moi/adresses/${encodeURIComponent(adresseId)}/confirmation`, { code });
  }
  /** Personne : annuler sa déclaration ou un changement d'adresse en carence, sans geste (motif facultatif). */
  annulerMonAdresse(adresseId: string, motif?: string): Promise<VueAdresse> {
    return this.poster(`/v0/moi/adresses/${encodeURIComponent(adresseId)}/annulation`, motif ? { motif } : {});
  }
  /** Administration : les adresses d'alerte du client (jamais un code). */
  async adressesDuClient(etat?: string): Promise<VueAdresseAdmin[]> {
    const r = await this.appel(`/v0/adresses${etat ? `?etat=${encodeURIComponent(etat)}` : ''}`, { cache: 'no-store' });
    return (await r.json()) as VueAdresseAdmin[];
  }
  /** Administration : déclarer l'adresse d'une autre personne (désignée par son sujet, du même émetteur). */
  declarerAdresse(sujet: string, adresse: string): Promise<AdresseDeclaree> {
    return this.poster('/v0/adresses', { sujet, adresse });
  }
  codeAdresseAdmin(adresseId: string): Promise<EnvoiCode> {
    return this.poster(`/v0/adresses/${encodeURIComponent(adresseId)}/code`);
  }
  annulerAdresse(adresseId: string, motif: string): Promise<VueAdresseAdmin> {
    return this.poster(`/v0/adresses/${encodeURIComponent(adresseId)}/annulation`, { motif });
  }
  desactiverAdresse(adresseId: string, motif: string): Promise<VueAdresseAdmin> {
    return this.poster(`/v0/adresses/${encodeURIComponent(adresseId)}/desactivation`, { motif });
  }

  // ------------------------------------------------------------ documents (chaîne documentaire, étape 2)

  livrables(cid: string): Promise<ResumeLivrable[]> {
    return this.json(`${this.c(cid)}/livrables`);
  }
  /** Un livrable avec sa version (ETag), à renvoyer pour le valider (If-Match). */
  async livrable(cid: string, lid: string): Promise<{ vue: VueLivrable; etag: string }> {
    const r = await this.appel(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}`);
    return { vue: (await r.json()) as VueLivrable, etag: r.headers.get('ETag') ?? '' };
  }
  /** Le livrable en Word (gabarit du client rempli, sources en commentaires) ou en PDF, tel que l'API le produit. */
  async telechargerLivrable(cid: string, lid: string, format: 'word' | 'pdf'): Promise<Blob> {
    return (await this.appel(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/${format}`)).blob();
  }
  /**
   * Premier temps de la validation d'un livrable : le plan de validation du contenu exact lu (son empreinte), calculé
   * et gardé par l'API (plan interne : aucun outil de l'entreprise n'est touché). Rien n'est signé. Le geste se fait
   * ensuite sur le défi de ce plan (`defiPlan`), comme pour une écriture.
   */
  planValidationLivrable(cid: string, lid: string, empreinteContenu: string): Promise<ReponsePlan> {
    return this.poster(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/validation/plan`, {
      empreinte_contenu: empreinteContenu,
    });
  }
  /**
   * Second temps : valide, par le geste fait sur le défi du plan gardé, le contenu exact lu (son empreinte), au nom de la
   * personne connectée ; un contenu qui a changé est refusé (409), un livrable relu entre-temps aussi (412).
   */
  async validerLivrable(
    cid: string,
    lid: string,
    d: {
      empreinte: string;
      commentaire?: string;
      etag: string;
      cle: string;
      plan_id: string;
      empreintePlan: string;
      defi_id: string;
      assertion: DemandeValidation['assertion'];
    },
  ): Promise<VueLivrable> {
    const r = await this.appel(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/validation`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': d.cle, 'If-Match': d.etag },
      body: JSON.stringify({
        empreinte_contenu: d.empreinte,
        commentaire: d.commentaire?.trim() || null,
        plan_id: d.plan_id,
        empreinte: d.empreintePlan,
        defi_id: d.defi_id,
        assertion: d.assertion,
      }),
    });
    return (await r.json()) as VueLivrable;
  }
  /**
   * Premier temps de la publication : le collecteur du site calcule le plan exact (il relit l'outil de l'entreprise avec
   * le compte gardé chez vous) ; l'API le garde pour vous. Rien n'est écrit ni signé. Le geste se fait ensuite sur le
   * défi de ce plan, puis `valider` (POST /validations) rend la validation signée.
   */
  planPublication(cid: string, lid: string): Promise<ReponsePlan> {
    return this.poster(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/publication/plan`);
  }
  /** Publication : le plan validé par un geste et sa validation signée, remis tels quels ; le collecteur l'exécute. */
  async publierLivrable(
    cid: string,
    lid: string,
    plan: PlanEcriture,
    validation: Validation,
    cle: string,
    attestation: Record<string, unknown> | null = null,
  ): Promise<ResultatPublication> {
    const r = await this.appel(`${this.c(cid)}/livrables/${encodeURIComponent(lid)}/publication`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': cle },
      // l'attestation rendue avec la validation voyage telle quelle jusqu'au connecteur, qui la vérifie (CV-3)
      body: JSON.stringify(attestation ? { plan, validation, attestation } : { plan, validation }),
    });
    return (await r.json()) as ResultatPublication;
  }
  licences(cid: string): Promise<VueLicences> {
    return this.json(`${this.c(cid)}/licences`);
  }

  // ------------------------------------------------------------ sources (docs/PIVOT-API.md § 8.11)

  /** Logiciels lisibles (connecteur ou recette d'export), coffres admis et seuils. */
  catalogueSources(): Promise<CatalogueSources> {
    return this.json('/v0/catalogue-sources');
  }
  sources(cid: string): Promise<SourceVue[]> {
    return this.json(`${this.c(cid)}/sources`);
  }
  /** Détail d'une source ; `avant` : numéro rendu en `lectures_suivantes`, pour la page plus ancienne de l'historique. */
  source(cid: string, sid: string, o: { avant?: number | null; limite?: number } = {}): Promise<SourceDetail> {
    const q = new URLSearchParams();
    if (o.limite) q.set('limite', String(o.limite));
    if (o.avant !== undefined && o.avant !== null) q.set('avant', String(o.avant));
    const s = q.toString();
    return this.json(`${this.c(cid)}/sources/${encodeURIComponent(sid)}${s ? `?${s}` : ''}`);
  }
  /**
   * Déclare une source (administration). Seule la RÉFÉRENCE du secret part, jamais sa valeur ; la requête n'est pas mise
   * en cache. Un 422 est rendu sans autre texte que celui de l'API (qui ne recopie pas la valeur reçue).
   */
  async declarerSource(cid: string, d: DeclarationSource, cleIdempotence: string): Promise<SourceVue> {
    try {
      const r = await this.appel(`${this.c(cid)}/sources`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': cleIdempotence },
        body: JSON.stringify(d),
      });
      return (await r.json()) as SourceVue;
    } catch (e) {
      // Un corps 422 non prévu (FastAPI standard) pourrait recopier la valeur reçue : on ne le montre pas.
      if (e instanceof ErreurApi && e.statut === 422 && e.code === 'erreur')
        throw new ErreurApi(422, 'requete_invalide', 'requête refusée : vérifiez les champs');
      throw e;
    }
  }
  /** Désactive (jamais de suppression : l'historique reste). Motif obligatoire. */
  async desactiverSource(cid: string, sid: string, motif: string): Promise<SourceVue> {
    return this.motif(cid, sid, 'desactivation', motif);
  }
  async reactiverSource(cid: string, sid: string, motif: string): Promise<SourceVue> {
    return this.motif(cid, sid, 'reactivation', motif);
  }
  private async motif(cid: string, sid: string, action: 'desactivation' | 'reactivation', motif: string): Promise<SourceVue> {
    const r = await this.appel(`${this.c(cid)}/sources/${encodeURIComponent(sid)}/${action}`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif }),
    });
    return (await r.json()) as SourceVue;
  }
  /** Demande un test de connexion (202) : le collecteur sur site l'exécute ; l'API ne se connecte à rien. */
  async demanderTest(cid: string, sid: string, cleIdempotence: string): Promise<EssaiVue> {
    const r = await this.appel(`${this.c(cid)}/sources/${encodeURIComponent(sid)}/tests`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Idempotency-Key': cleIdempotence },
    });
    return (await r.json()) as EssaiVue;
  }
  essaiSource(cid: string, sid: string, tid: string): Promise<EssaiVue> {
    return this.json(`${this.c(cid)}/sources/${encodeURIComponent(sid)}/tests/${encodeURIComponent(tid)}`);
  }

  // ------------------------------------------------------------ collecteurs (administration, CONCEPTION-DEPOTS-SIGNES § 10)

  collecteurs(): Promise<CollecteurVue[]> {
    return this.json('/v0/collecteurs');
  }
  /** Un collecteur ; `avant` : curseur opaque rendu en `journal_suivant`, pour la page plus ancienne du journal. */
  collecteur(id: string, o: { avant?: string | null; limite?: number } = {}): Promise<CollecteurDetail> {
    const q = new URLSearchParams();
    if (o.limite) q.set('limite', String(o.limite));
    if (o.avant) q.set('avant', o.avant);
    const s = q.toString();
    return this.json(`/v0/collecteurs/${encodeURIComponent(id)}${s ? `?${s}` : ''}`);
  }
  /**
   * Une écriture d'administration sur un collecteur (jamais de suppression : aucune méthode DELETE). Pas de cache ; un 422 au
   * format standard de FastAPI pourrait recopier la valeur reçue (l'empreinte saisie) : il n'est jamais montré.
   */
  private async ecrireCollecteur(chemin: string, corps: unknown, cleIdempotence: string): Promise<CollecteurVue> {
    try {
      const r = await this.appel(chemin, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': cleIdempotence },
        body: JSON.stringify(corps),
      });
      return (await r.json()) as CollecteurVue;
    } catch (e) {
      if (e instanceof ErreurApi && e.statut === 422 && e.code === 'erreur')
        throw new ErreurApi(422, 'requete_invalide', 'requête refusée : vérifiez les champs');
      throw e;
    }
  }
  /** Approuve une demande d'enrôlement : `empreinteCourte` = 16 hexadécimaux normalisés, lus sur le serveur du collecteur. */
  approuverCollecteur(id: string, empreinteCourte: string, nom: string, cle: string): Promise<CollecteurVue> {
    return this.ecrireCollecteur(`/v0/collecteurs/${encodeURIComponent(id)}/approbation`, { empreinte_courte: empreinteCourte, nom }, cle);
  }
  refuserCollecteur(id: string, motif: string, cle: string): Promise<CollecteurVue> {
    return this.ecrireCollecteur(`/v0/collecteurs/${encodeURIComponent(id)}/refus`, { motif }, cle);
  }
  desactiverCollecteur(id: string, motif: string, cle: string): Promise<CollecteurVue> {
    return this.ecrireCollecteur(`/v0/collecteurs/${encodeURIComponent(id)}/desactivation`, { motif }, cle);
  }
  reactiverCollecteur(id: string, motif: string, cle: string): Promise<CollecteurVue> {
    return this.ecrireCollecteur(`/v0/collecteurs/${encodeURIComponent(id)}/reactivation`, { motif }, cle);
  }
  /** Révocation définitive ; `compromisDepuis` (horodatage UTC) seulement avec la raison « compromission ». */
  revoquerCollecteur(
    id: string,
    d: { motif: string; raison: RaisonRevocation; compromisDepuis?: string | null },
    cle: string,
  ): Promise<CollecteurVue> {
    const corps: Record<string, unknown> = { motif: d.motif, raison: d.raison };
    if (d.compromisDepuis) corps.compromis_depuis = d.compromisDepuis;
    return this.ecrireCollecteur(`/v0/collecteurs/${encodeURIComponent(id)}/revocation`, corps, cle);
  }
  /** Lie une source à un collecteur actif (POST …/sources/{sid}/collecteur) ; une personne suffit, motif obligatoire. */
  async lierSource(cid: string, sid: string, collecteur: string, motif: string, cle: string): Promise<SourceVue> {
    const r = await this.appel(`${this.c(cid)}/sources/${encodeURIComponent(sid)}/collecteur`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': cle },
      body: JSON.stringify({ collecteur, motif }),
    });
    return (await r.json()) as SourceVue;
  }

  // ------------------------------------------------------------ dépôt des documents (offre Diagnostic)

  /** Crée une carte vide pour commencer un diagnostic (rôle administration). */
  creerCarte(id: string, entreprise: string): Promise<ResumeCarte> {
    return this.poster('/v0/cartes', { id, entreprise });
  }
  /** Les pièces déposées, leur état (reçue, lue, refusée avec sa raison) et la dernière lecture. */
  async depots(cid: string): Promise<VueDepots> {
    const r = await this.appel(`${this.c(cid)}/depots`, { cache: 'no-store' });
    return (await r.json()) as VueDepots;
  }
  /**
   * Dépose une pièce : le fichier brut est envoyé tel quel (l'API reconnaît son type au contenu, jamais au nom). Le nom
   * du fichier ne sert qu'à l'affichage ; il est encodé pour passer dans un en-tête.
   */
  async deposer(cid: string, fichier: Blob, nom: string, nature: NatureDepot, f: { titre?: string; date?: string } = {}): Promise<PieceDeposee> {
    const q = new URLSearchParams({ nature });
    if (f.titre?.trim()) q.set('titre', f.titre.trim());
    if (f.date) q.set('date', f.date);
    const r = await this.appel(`${this.c(cid)}/depots?${q.toString()}`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/octet-stream', 'Ekera-Nom-Fichier': encodeURIComponent(nom) },
      body: fichier,
    });
    return (await r.json()) as PieceDeposee;
  }
  /** Lit toutes les pièces et met la carte à jour (en arrière-plan : suivre l'état par `depots`). */
  lirePieces(cid: string): Promise<EtatLecturePieces> {
    return this.poster(`${this.c(cid)}/depots/lecture`);
  }

  // ------------------------------------------------------------ entretien en ligne (offre Diagnostic)

  entretien(cid: string): Promise<EtatEntretien> {
    return this.json(`${this.c(cid)}/entretien`);
  }
  /** Prépare (ou met à jour) les questions sur la carte actuelle. */
  preparerEntretien(cid: string): Promise<EtatEntretien> {
    return this.poster(`${this.c(cid)}/entretien/preparation`);
  }
  /** Répond à une question (ou « je ne sais pas ») ; la carte est mise à jour par l'API. */
  repondreEntretien(cid: string, qid: string, r: { reponse?: string; inconnu?: boolean }): Promise<ResultatReponseEntretien> {
    return this.poster(`${this.c(cid)}/entretien/questions/${encodeURIComponent(qid)}/reponse`, r);
  }
  /** Délègue une question à un collègue : il devra se connecter pour répondre. */
  delegerQuestion(cid: string, qid: string, destinataire: string): Promise<ReponseDelegation> {
    return this.poster(`${this.c(cid)}/entretien/questions/${encodeURIComponent(qid)}/delegation`, { destinataire });
  }
  questionDeleguee(cid: string, did: string): Promise<QuestionDeleguee> {
    return this.json(`${this.c(cid)}/entretien/delegations/${encodeURIComponent(did)}`);
  }
  repondreDelegation(cid: string, did: string, r: { reponse?: string; inconnu?: boolean }): Promise<QuestionEntretien> {
    return this.poster(`${this.c(cid)}/entretien/delegations/${encodeURIComponent(did)}/reponse`, r);
  }

  // ------------------------------------------------------------ rapport de diagnostic (offre Diagnostic)

  rapports(cid: string): Promise<ResumeRapport[]> {
    return this.json(`${this.c(cid)}/rapports`);
  }
  /** Génère le rapport de diagnostic (PDF) de la carte telle qu'elle est ; l'API le garde. */
  genererRapport(cid: string): Promise<ResumeRapport> {
    return this.poster(`${this.c(cid)}/rapports`);
  }
  async telechargerRapport(cid: string, rid: string): Promise<Blob> {
    return (await this.appel(`${this.c(cid)}/rapports/${encodeURIComponent(rid)}/pdf`)).blob();
  }

  /** POST /corrections : simulation (aucune écriture) ou écriture validée. */
  async corriger(cid: string, correction: Correction, o: OptionsEcriture): Promise<ResultatEcriture> {
    const enTetes: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!o.simulation) {
      if (!o.cleIdempotence) throw new Error('clé d’idempotence obligatoire pour écrire');
      enTetes['Idempotency-Key'] = o.cleIdempotence;
    }
    if (o.ifMatch) enTetes['If-Match'] = o.ifMatch;
    const r = await this.appel(`${this.c(cid)}/corrections${o.simulation ? '?simulation=true' : ''}`, {
      method: 'POST',
      headers: enTetes,
      body: JSON.stringify(correction),
    });
    return {
      reponse: (await r.json()) as ReponseEcriture,
      statut: r.status,
      rejoue: r.headers.get('Idempotent-Replay') === 'true',
    };
  }
}

/** Clé d'idempotence nouvelle (une par écriture proposée, réutilisée pour un nouvel essai). */
export function nouvelleCle(): string {
  return `webapp-${crypto.randomUUID()}`;
}
